import { Col, Row, Spin } from 'antd';
import React, {
  FC, useCallback, useContext, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useAppDispatch } from '../../hooks/redux-hooks';
import { selectBookingAppointmentData, selectBookingData, updateBookingAppointmentData } from '../../redux/slices/booking';
import { BookingModal, ColWithBar } from './style';
import TitleWithIcon from '../TitleWithIcon';
import SelectedTime from '../SelectedTimeDisplaySection';
import BookingCalendar from '../BookingCalendar';
import AvailableTimeslotList from '../AvailableTimeslotList';
import Specialist from '../../model/Specialist';
import { AppointmentData, AppointmentType } from '../../model';
import { PATIENT_DETAILS, PHN_PATIENT_DETAILS, PHN_ROUTE } from '../../constants/routes';
import { useGetAvailabilitiesBySpecialistQuery } from '../../services/availabilities';
import AvailableTime from '../../model/AvailableTime';
import { BookingTimeContext } from '../SpecialistCard';
import analytics from '../../infra/analytics';
import { getSpecialistStepConfig } from '../../config/privateBooking';

// eslint-disable-next-line no-unused-vars
const SelectBookingTimeModal: FC<{ specialist: Specialist, isBookingTimeModalOpen: boolean, setIsBookingTimeModalOpen: (isOpen: boolean) => void }> = ({ specialist, isBookingTimeModalOpen, setIsBookingTimeModalOpen }) => {
  const { type } = useSelector(selectBookingAppointmentData) as AppointmentData;
  const { metadata } = useSelector(selectBookingData);
  const specialistConfig = getSpecialistStepConfig();
  const { bookingTime, updateBookingTime } = useContext(BookingTimeContext);
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const { appointmentLength, id: specialistId, firstName: specalistFirtName, lastName: specalistLastName } = specialist;
  const appointmentType = useMemo(() => type ?? AppointmentType.NEW, [type]);
  const navigate = useNavigate();

  const aptLength = useMemo(() => appointmentLength?.split('-')[0], [appointmentLength]) as string;

  const dispatch = useAppDispatch();
  const { search, pathname } = useLocation();
  const requiresCentreSelection = specialistConfig?.requireCentreSelection ?? false;
  const disableOk = requiresCentreSelection && !metadata?.centreId;

  const handleOK = useCallback(() => {
    const { month, date, startTime, slotId, startDateTime, endDateTime } = bookingTime;
    setIsBookingTimeModalOpen(false);
    if (month && date && startTime) {
      dispatch(updateBookingAppointmentData({
        date,
        startTime,
        aptLength,
        specialistId,
        specalistFirtName,
        specalistLastName,
        ...(slotId ? { slotId } : {}),
        ...(startDateTime ? { startDateTime } : {}),
        ...(endDateTime ? { endDateTime } : {}),
      }));

      // Track time selected event
      analytics.trackVpcTimeSelected({
        time_slot: `${date} ${startTime}`,
        page_path: pathname,
      });

      const nextRoute = pathname.includes(`${PHN_ROUTE}`) ? PHN_PATIENT_DETAILS : PATIENT_DETAILS;
      navigate(`${nextRoute}${search}`);
    }
  }, [dispatch, bookingTime, aptLength, specialistId, navigate, setIsBookingTimeModalOpen, search, pathname, specalistFirtName, specalistLastName]);
  const handleCancel = useCallback(() => setIsBookingTimeModalOpen(false), [setIsBookingTimeModalOpen]);

  const { isLoading: isLoadingAvailableTimeList, data: availableTimeList } = useGetAvailabilitiesBySpecialistQuery({
    specialistId,
    appointmentType,
    month: bookingTime.month,
    aptLength,
    locationId: metadata?.centreId,
  });

  const renderDesktop = () => (
    <Row>
      <ColWithBar span={15} style={{ height: '600px', overflow: 'scroll' }}>
        <AvailableTimeslotList
          bookingTime={bookingTime}
          updateBookingTime={updateBookingTime}
          aptLength={aptLength}
          availableTimeList={availableTimeList as AvailableTime[]}
        />
      </ColWithBar>
      <Col span={8}>
        <SelectedTime bookingTime={bookingTime} />
        <Spin spinning={isLoadingAvailableTimeList} tip="loading...">
          <BookingCalendar
            availableTimeList={availableTimeList as AvailableTime[]}
            bookingTime={bookingTime}
            updateBookingTime={updateBookingTime}
          />
        </Spin>
      </Col>
    </Row>
  );

  const renderMobile = () => (
    [
      <SelectedTime bookingTime={bookingTime} />,
      <Spin spinning={isLoadingAvailableTimeList} tip="loading...">
        <BookingCalendar
          availableTimeList={availableTimeList as AvailableTime[]}
          bookingTime={bookingTime}
          updateBookingTime={updateBookingTime}
        />
      </Spin>,
      <AvailableTimeslotList
        bookingTime={bookingTime}
        updateBookingTime={updateBookingTime}
        aptLength={aptLength}
        availableTimeList={availableTimeList as AvailableTime[]}
      />,
    ]
  );

  return (
    <BookingModal
      visible={isBookingTimeModalOpen}
      width={isMobile ? '95%' : '70%'}
      onCancel={handleCancel}
      onOk={handleOK}
      okButtonProps={{ disabled: disableOk }}
      centered
    >
      <div style={{ marginBottom: '20px', overflow: 'scroll' }}>
        <TitleWithIcon icon="table" text="Select an Appointment Time" />
        {(specialistId && availableTimeList) && (isMobile ? renderMobile() : renderDesktop())}
      </div>
    </BookingModal>
  );
};

export default SelectBookingTimeModal;
